import { useCallback, useMemo, useRef, useState } from 'react';

import { CollisionPriority } from '@dnd-kit/abstract';
import { AutoScroller, Cursor, Feedback, PreventSelection } from '@dnd-kit/dom'; // Draggable App container header
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { arrayMove, move } from '@dnd-kit/helpers';
import { DragDropProvider, useDroppable } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import { AddOutlined, ClearOutlined, DragIndicator, SettingsOutlined } from '@mui/icons-material';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import { Box, Tab, Tabs, Tooltip } from '@mui/material';

import { ChartProvider } from '@/components/Chart/context/ChartContext'; // Draggable App container header // Draggable App container header
import { MarketWidget } from '@/components/MarketWidget';
import TooltipIconButton from '@/components/TooltipIconButton';
import Typography from '@/components/Typography';
import { useRemoveWidgetContext } from '@/context/RemoveWidgetContext';
import { NameInput } from '@/features/Dashboard/components/NameInput';
import { TABNAME_MAX_LENGTH } from '@/features/Dashboard/constants/const';
import { useEditDashboardDraft } from '@/features/Dashboard/context/EditDashboardDraft';
import { DrawerStep, useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import { AppType, MarketAppWithTabs, WidgetV2 } from '@/features/Dashboard/types';
import AppFormAdapter from '@/features/Dashboard/utils/AppFormAdapter'; // Draggable App container header
import { createNameSchema } from '@/features/Dashboard/utils/validateName';
import { MarketWidgetProvider } from '@/pages/Markets/MarketsComponents/contexts/MarketWidgetContext';
import { SetState } from '@/types/helpers';
import { extractIds } from '@/utils/extractIds';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

import MoveAppToDashboardMenu from './MoveAppToDashboardMenu';
import MoveToDashboardMenu from './MoveToDashboardMenu';

// Draggable App container header
const BLANK_COLUMN_ID = '__blank__';

const Item = ({
  index,
  item,
  column,
  onRemoveItem,
  container,
  isSingleItem,
  onEdit,
  currentDashboardName,
  currentTabName,
  onMoveToDraft
}: {
  index: number;
  item: WidgetV2;
  column: string;
  onRemoveItem: (id: string) => void;
  onEdit: (tabId: string) => void;
  container: React.RefObject<HTMLDivElement | null>;
  isSingleItem: boolean;
  currentDashboardName: string;
  currentTabName: string;
  onMoveToDraft: (tabName: string, widget: WidgetV2) => void;
}) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [moveMenuAnchor, setMoveMenuAnchor] = useState<HTMLElement | null>(null);

  const handleRef = useRef<HTMLButtonElement | null>(null);
  useSortable({
    id: item.id,
    element: elementRef,
    index,
    handle: handleRef,
    type: isSingleItem ? 'singleItem' : 'item',
    accept: 'item',
    group: column,
    modifiers: [RestrictToElement.configure({ element: container.current })]
  });
  const { openRemoveModal } = useRemoveWidgetContext();
  const { setMarketTabApps } = useEditDashboardDraft();

  const nameErrors = useMemo(() => {
    const res = createNameSchema('Name', TABNAME_MAX_LENGTH).safeParse(item.name ?? '');
    if (res.success) return undefined;
    return res.error;
  }, [item.name]);

  const isEditable =
    (item?.type === 'graph' &&
      (item.metadata?.graphType === 'forwardSimple' ||
        item.metadata?.graphType === 'seasonal' ||
        !item.metadata?.graphType)) ||
    item.type === 'table';

  return (
    <Tab
      ref={elementRef}
      disabled
      component='div'
      label={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title='Drag to reorder tab'>
            <Box
              ref={handleRef}
              className='tab-drag-handle'
              sx={{
                width: 16,
                height: 46,
                p: 0,
                borderRadius: 0,
                backgroundColor: 'gx.response',
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'grab'
              }}
            >
              <DragIndicator sx={{ fontSize: '14px' }} />
            </Box>
          </Tooltip>

          <Box
            sx={{
              maxWidth: '300px',
              '.MuiInputBase-root': {
                height: '46px'
              },
              '.MuiTextField-root': {
                minWidth: 'auto',
                display: 'flex',

                input: {
                  color: 'gx.textDisabled',
                  '&:focus': {
                    color: 'text.primary'
                  },
                  '&:hover': {
                    color: 'text.primary'
                  }
                }
              },

              '.MuiButtonBase-root': {
                display: 'none'
              },
              '.error-message': {
                backgroundColor: 'background.paper',
                px: 0.5,
                mt: 0
              }
            }}
          >
            <NameInput
              error={nameErrors}
              maxLength={TABNAME_MAX_LENGTH}
              value={item.name ?? ''}
              onChange={nextName => {
                setMarketTabApps(apps =>
                  apps.map(app =>
                    app.id === column
                      ? {
                          ...app,
                          tabs: app.tabs.map(t => (t.id === item.id ? { ...t, name: nextName } : t))
                        }
                      : app
                  )
                );
              }}
            />
          </Box>
          <Box
            className='edit-buttons__container'
            sx={{
              display: 'flex',
              ml: 'auto',
              gap: '2px',
              '& button': {
                color: 'text.primary',
                '& svg': {
                  color: 'text.primary'
                }
              }
            }}
          >
            {/*<TooltipIconButton*/}
            {/*  sx={{ height: 46 }}*/}
            {/*  title='Move to ...'*/}
            {/*  onClick={e => {*/}
            {/*    setMoveMenuAnchor(e.currentTarget);*/}
            {/*  }}*/}
            {/*>*/}
            {/*  <HighlightAltOutlined />*/}
            {/*</TooltipIconButton>*/}
            {isEditable && (
              <TooltipIconButton
                sx={{ height: 46 }}
                title='Configuration settings'
                onClick={() => {
                  onEdit(item.id);
                }}
              >
                <SettingsOutlined />
              </TooltipIconButton>
            )}
            <TooltipIconButton
              sx={{ height: 46 }}
              title='Remove tab'
              onClick={() => {
                openRemoveModal(() => {
                  onRemoveItem(item.id);
                });
              }}
            >
              <ClearOutlined />
            </TooltipIconButton>
          </Box>
          <MoveToDashboardMenu
            anchorEl={moveMenuAnchor}
            currentDashboardName={currentDashboardName}
            currentTabName={currentTabName}
            open={Boolean(moveMenuAnchor)}
            widget={item}
            onClose={() => {
              setMoveMenuAnchor(null);
            }}
            onMoveToDraft={onMoveToDraft}
            onRemoveFromCurrent={() => {
              onRemoveItem(item.id);
            }}
          />
        </Box>
      }
      sx={{
        '&.Mui-disabled': {
          pointerEvents: 'auto'
        },
        overflow: 'visible',
        minWidth: '180px',
        maxWidth: 'initial',
        whiteSpace: 'nowrap',
        position: 'relative',
        textTransform: 'initial',
        opacity: '1 !important',
        height: 48,
        p: 0,
        borderWidth: '1px',
        borderStyle: 'dashed !important',
        borderColor: 'primary.main',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexDirection: 'row',
        gap: 1,
        color: 'text.primary',
        '&:hover': {
          color: 'inherit'
        },
        outline: '0px solid transparent',
        transition: 'outline-color 120ms ease, outline-width 120ms ease',
        '&[data-tab-drop-over="true"]': {
          outlineColor: 'primary.main',
          outlineWidth: '2px'
        }
      }}
    />
  );
};
const Column = ({
  id,
  index,
  items,
  onRemoveApp,
  onRemoveItem,
  container,
  currentDashboardName,
  currentTabName,
  onMoveToDraft,
  onMoveAppToDraft,
  app
}: {
  id: string;
  index: number;
  items: WidgetV2[];

  onRemoveApp: (id: string) => void;
  onRemoveItem: (id: string) => void;
  container: React.RefObject<HTMLDivElement | null>;
  currentDashboardName: string;
  currentTabName: string;
  onMoveToDraft: (tabName: string, widget: WidgetV2) => void;
  onMoveAppToDraft: (tabName: string, app: MarketAppWithTabs) => void;
  app: MarketAppWithTabs;
}) => {
  const handleRef = useRef<HTMLButtonElement | null>(null);
  const [moveAppMenuAnchor, setMoveAppMenuAnchor] = useState<HTMLElement | null>(null);
  const { ref } = useSortable({
    id,
    index,
    handle: handleRef,
    type: 'column',
    collisionPriority: CollisionPriority.Low,
    accept: ['column'],
    modifiers: [RestrictToElement.configure({ element: container.current })]
  });
  const { ref: itemDropRef } = useDroppable({
    id: `${id}-item-drop`,
    type: 'column-item-drop',
    accept: ['item', 'singleItem'],
    collisionPriority: CollisionPriority.Low,
    data: { columnId: id } // 👈 Przekazujemy columnId w data
  });
  const { setDrawerStep, setIsEditMode, setEditingTarget } = useDrawerSteps();
  const { setInitialValues } = useAppForm();

  const { preview, setInsertAtId } = useEditDashboardDraft();

  const tabContent = useMemo(() => {
    const tabId = items[0]?.id;
    if (!tabId) return;

    const appWithTab = preview?.components?.find((app: any) => {
      return 'tabs' in app && app.tabs?.some((tab: any) => tab.id === tabId);
    });

    return appWithTab?.tabs?.find((tab: any) => tab.id === tabId);
  }, [preview, items]);

  const handleEdit = useCallback(
    (tabId: string) => {
      const app = items.find(app => app.id === tabId);
      if (!app) return;
      const values = AppFormAdapter.toFormValues(app);
      setIsEditMode(true);
      setEditingTarget({ appId: id, tabId });
      setInitialValues(values);
      switch (values.type) {
        case AppType.Table:
        case AppType.Tiles:
          setDrawerStep(DrawerStep.SelectBasicDataTable);
          break;
        case AppType.FWD:
          setDrawerStep(DrawerStep.SelectFWDChart);
          break;
        case AppType.Spot:
        case AppType.Seasonal:
          setDrawerStep(DrawerStep.SelectSpotChart);
          break;
        default:
          throw new Error(`Not editable app type`);
      }
    },
    [items, setIsEditMode, setEditingTarget, id, setInitialValues, setDrawerStep]
  );

  const { openRemoveModal } = useRemoveWidgetContext();

  return (
    <Box
      ref={ref}
      sx={{
        border: '4px dashed',
        borderColor: 'gx.textDisabled',
        position: 'relative',
        overflow: 'hidden',
        pt: '2px',
        backgroundColor: 'background.paper'
      }}
    >
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <TooltipIconButton
          ref={handleRef}
          sx={{ cursor: 'grab', width: 24 }}
          title='Drag to reorder app'
        >
          <DragIndicatorOutlinedIcon sx={{ fontSize: '21px !important' }} />
        </TooltipIconButton>
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            display: 'flex'
          }}
        >
          <Tabs
            aria-label='tabs'
            scrollButtons='auto'
            sx={{
              '& .MuiTabs-scrollButtons.Mui-disabled': {
                display: 'none'
              },
              '.name-input': {
                flexDirection: 'column'
              },
              '.error-message': {
                position: 'absolute',
                top: 0
              },
              mr: 0.5
            }}
            variant='scrollable'
          >
            {items.map((item, index) => (
              <Item
                key={item.id}
                column={id}
                container={container}
                currentDashboardName={currentDashboardName}
                currentTabName={currentTabName}
                index={index}
                isSingleItem={items.length === 1}
                item={item}
                onEdit={handleEdit}
                onMoveToDraft={onMoveToDraft}
                onRemoveItem={onRemoveItem}
              />
            ))}
          </Tabs>
          <TooltipIconButton
            sx={{ width: 32, backgroundColor: 'gx.tileLight' }}
            title='Add new app'
            onClick={() => {
              setInsertAtId(id);
              setIsEditMode(true);
              setEditingTarget(null);
              setDrawerStep(DrawerStep.SelectApp);
            }}
          >
            <AddOutlined sx={{ fontSize: '14px !important', color: 'gx.textDisabled' }} />
          </TooltipIconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: '2px' }}>
          {/*<TooltipIconButton*/}
          {/*  title='Move to ...'*/}
          {/*  onClick={e => {*/}
          {/*    setMoveAppMenuAnchor(e.currentTarget);*/}
          {/*  }}*/}
          {/*>*/}
          {/*  <HighlightAltOutlined />*/}
          {/*</TooltipIconButton>*/}
          <TooltipIconButton
            title='Remove app'
            onClick={() => {
              openRemoveModal(() => {
                onRemoveApp(id);
              });
            }}
          >
            <ClearOutlined />
          </TooltipIconButton>
          <MoveAppToDashboardMenu
            anchorEl={moveAppMenuAnchor}
            app={app}
            currentDashboardName={currentDashboardName}
            currentTabName={currentTabName}
            open={Boolean(moveAppMenuAnchor)}
            onClose={() => {
              setMoveAppMenuAnchor(null);
            }}
            onMoveToDraft={onMoveAppToDraft}
            onRemoveFromCurrent={() => {
              onRemoveApp(id);
            }}
          />
        </Box>
      </Box>
      <Box
        ref={itemDropRef}
        sx={{ flexGrow: 1, position: 'relative', mt: 1.5 }}
      >
        {tabContent && (
          <MarketWidgetProvider>
            <ChartProvider>
              <MarketWidget tab={tabContent} />
            </ChartProvider>
          </MarketWidgetProvider>
        )}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            backgroundColor: 'rgba(18, 24, 54, 0.80)',
            zIndex: 9
          }}
        />
      </Box>
    </Box>
  );
};

const BlankColumn = () => {
  const { ref, isDropTarget } = useDroppable({
    id: BLANK_COLUMN_ID,
    type: 'new-column',
    accept: ['item'],
    collisionPriority: CollisionPriority.Low
  });

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        display: 'flex',
        backgroundColor: isDropTarget ? 'rgba(53, 72, 142, 0.25)' : 'background.paper',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '40vh',
        border: '4px dashed',
        borderColor: isDropTarget ? 'gx.textDisabled' : 'transparent',
        transition: 'background-color 120ms ease, outline-color 120ms ease'
      }}
    >
      <Typography
        size={14}
        sx={{
          color: 'gx.textDisabled'
        }}
      >
        Drop a tab here
      </Typography>
    </Box>
  );
};

export type AppsOrderProps = {
  apps: MarketAppWithTabs[];
  onChange: SetState<MarketAppWithTabs[]>;
  marketData: any;
};

// AppsOrder allows: reorder apps, reorder tabs within app,
// move tab to another app.
export const AppsOrder = ({
  onTabCreated
}: {
  onTabCreated?: (tabName: string, tabId: string) => void;
}) => {
  const {
    dashboardName,
    currentTabName,
    marketTabApps,
    setMarketTabApps,
    setIsDragging,
    setTabDrafts,
    tabDrafts,
    getAllTabDrafts,
    draftTabNames: editDraftTabNames,
    draftTabs: editDraftTabs
  } = useEditDashboardDraft();
  const container = useRef<HTMLDivElement | null>(null);
  const shouldShowBlankColumn = marketTabApps.some(app => app.tabs.length > 1);

  // Get draft tab names - use names from edit mode if provided, otherwise use IDs
  const draftTabNames = useMemo(() => {
    if (editDraftTabNames && editDraftTabNames.length > 0) {
      // Use actual tab names from edit mode
      return editDraftTabNames;
    }
    // Fallback: use IDs from drafts (shouldn't happen in normal flow)
    const allDrafts = getAllTabDrafts();
    return Object.keys(allDrafts);
  }, [editDraftTabNames, getAllTabDrafts]);

  const handleMoveToDraft = useCallback(
    (tabName: string, widget: WidgetV2) => {
      // Find tab ID by name using draftTabs mapping
      const targetTab = editDraftTabs?.find(tab => tab.name === tabName);
      let targetTabId = targetTab?.id;

      // If tab doesn't exist, create a new one
      if (!targetTabId) {
        // Generate a new tab ID
        const existingTabIds = new Set(editDraftTabs?.map(tab => tab.id) || []);
        targetTabId = generateUniqueUUID(existingTabIds);

        // Notify parent to add the tab to draftTabs
        if (onTabCreated) {
          onTabCreated(tabName, targetTabId);
        }
      }

      // Get the draft for the target tab using tab ID (not name)
      const targetTabDraft = tabDrafts[targetTabId];

      // Convert widget to MarketApp format
      const existingAppIds = new Set<string>();
      const existingTabIds = new Set<string>();

      // Collect existing IDs
      Object.values(tabDrafts).forEach(draft => {
        draft.marketTabApps.forEach(app => {
          if (app.id) existingAppIds.add(app.id);
          app.tabs.forEach(tab => {
            if (tab.id) existingTabIds.add(tab.id);
          });
        });
      });

      const newAppId = generateUniqueUUID(existingAppIds);
      const newTabId = generateUniqueUUID(existingTabIds);

      const widgetApp: MarketAppWithTabs = {
        id: newAppId,
        tabs: [{ ...widget, id: newTabId }]
      };

      // Update the target tab's draft
      const updatedTargetDraft = {
        marketTabApps: targetTabDraft ? [...targetTabDraft.marketTabApps, widgetApp] : [widgetApp],
        tiles: targetTabDraft?.tiles || []
      };

      setTabDrafts(prev => ({
        ...prev,
        [targetTabId]: updatedTargetDraft
      }));
    },
    [tabDrafts, setTabDrafts, editDraftTabs, onTabCreated]
  );

  const handleMoveAppToDraft = useCallback(
    (tabName: string, app: MarketAppWithTabs) => {
      // Find tab ID by name using draftTabs mapping
      const targetTab = editDraftTabs?.find(tab => tab.name === tabName);
      const targetTabId = targetTab?.id;

      if (!targetTabId) {
        console.error(`Could not find tab ID for tab name: ${tabName}`);
        return;
      }

      // Get the draft for the target tab using tab ID (not name)
      const targetTabDraft = tabDrafts[targetTabId];

      // Generate new IDs for the app and all its tabs
      const existingAppIds = new Set<string>();
      const existingTabIds = new Set<string>();

      // Collect existing IDs
      Object.values(tabDrafts).forEach(draft => {
        draft.marketTabApps.forEach(app => {
          if (app.id) existingAppIds.add(app.id);
          app.tabs.forEach(tab => {
            if (tab.id) existingTabIds.add(tab.id);
          });
        });
      });

      const newAppId = generateUniqueUUID(existingAppIds);
      const newTabs = app.tabs.map(tab => ({
        ...tab,
        id: generateUniqueUUID(existingTabIds)
      }));

      const appWithNewIds: MarketAppWithTabs = {
        id: newAppId,
        tabs: newTabs
      };

      // Update the target tab's draft
      const updatedTargetDraft = {
        marketTabApps: targetTabDraft
          ? [...targetTabDraft.marketTabApps, appWithNewIds]
          : [appWithNewIds],
        tiles: targetTabDraft?.tiles || []
      };

      setTabDrafts(prev => ({
        ...prev,
        [targetTabId]: updatedTargetDraft
      }));
    },
    [tabDrafts, setTabDrafts, editDraftTabs]
  );
  return (
    <DragDropProvider
      plugins={[Feedback, AutoScroller, Cursor, PreventSelection]}
      onDragEnd={event => {
        const operation = (event as any)?.operation;
        const { source, target } = operation ?? {};

        if (source?.type === 'item' || source?.type === 'singleItem') {
          // Dropped on blank column - create new app
          if (target?.id === BLANK_COLUMN_ID || target?.group === BLANK_COLUMN_ID) {
            setMarketTabApps(items => {
              const tabId = source.id as string;
              const hasTab = (app: MarketAppWithTabs) => app.tabs.some(t => t.id === tabId);
              const appContainingTab = items.find(hasTab);
              if (!appContainingTab) return items;

              if (appContainingTab.tabs.length === 1) return items;

              const tab = appContainingTab.tabs.find(t => t.id === tabId)!;
              const next = items
                .map(app => ({ ...app, tabs: app.tabs.filter(t => t.id !== tabId) }))
                .filter(app => app.tabs.length > 0);

              const appIds = extractIds(items);
              const newAppId = generateUniqueUUID(appIds);
              next.push({ id: newAppId, tabs: [tab] });
              return next;
            });
          }
          // 👇 Dropped on column item drop zone - add tab to that column
          else if (target?.type === 'column-item-drop') {
            setMarketTabApps(items => {
              const sourceTabId = source.id as string;
              const targetColumnId = target.data?.columnId as string;

              if (!targetColumnId) return items;

              let draggedTab: WidgetV2 | undefined;
              let sourceColumnId: string | undefined;

              for (const app of items) {
                const tab = app.tabs.find(t => t.id === sourceTabId);
                if (tab) {
                  draggedTab = tab;
                  sourceColumnId = app.id;
                  break;
                }
              }

              if (!draggedTab || !sourceColumnId) return items;
              if (sourceColumnId === targetColumnId) return items;

              return items
                .map(app => {
                  if (app.id === sourceColumnId) {
                    return { ...app, tabs: app.tabs.filter(t => t.id !== sourceTabId) };
                  }
                  if (app.id === targetColumnId) {
                    if (app.tabs.some(t => t.id === sourceTabId)) return app;
                    return { ...app, tabs: [...app.tabs, draggedTab] };
                  }
                  return app;
                })
                .filter(app => app.tabs.length > 0);
            });
          }
        }

        // Clean up after drag ends
        setMarketTabApps(items => items.filter(app => app.tabs.length > 0));
        setIsDragging(false);
      }}
      onDragOver={event => {
        const { source, target } = event.operation as any;
        if (target?.id === undefined) return;

        if (source?.type === 'column') {
          if ((event as any).operation?.target?.id === BLANK_COLUMN_ID) {
            return;
          }
          setMarketTabApps(items => move(items, event as any));
          return;
        }

        if (source?.type === 'item' || source?.type === 'singleItem') {
          if (target?.id === BLANK_COLUMN_ID || target?.group === BLANK_COLUMN_ID) {
            return;
          }

          // Ignoruj gdy item jest nad column (sortable)
          if (target?.type === 'column') {
            return;
          }

          // 👇 Item nad column drop zone - przenieś tab do tej column
          if (target?.type === 'column-item-drop') {
            setMarketTabApps(items => {
              const sourceTabId = source.id as string;
              const targetColumnId = target.data?.columnId as string;

              if (!targetColumnId) return items;

              let draggedTab: WidgetV2 | undefined;
              let sourceColumnId: string | undefined;

              for (const app of items) {
                const tab = app.tabs.find(t => t.id === sourceTabId);
                if (tab) {
                  draggedTab = tab;
                  sourceColumnId = app.id;
                  break;
                }
              }

              if (!draggedTab || !sourceColumnId) return items;
              if (sourceColumnId === targetColumnId) return items;

              // Sprawdź czy tab już jest w target column
              const targetApp = items.find(app => app.id === targetColumnId);
              if (targetApp?.tabs.some(t => t.id === sourceTabId)) return items;

              return items.map(app => {
                if (app.id === sourceColumnId) {
                  return { ...app, tabs: app.tabs.filter(t => t.id !== sourceTabId) };
                }
                if (app.id === targetColumnId) {
                  return { ...app, tabs: [...app.tabs, draggedTab] };
                }
                return app;
              });
            });
            return;
          }

          // Item nad item - manualna logika
          if (target?.type === 'item' || target?.type === 'singleItem') {
            setMarketTabApps(items => {
              const sourceTabId = source.id as string;
              const targetTabId = target.id as string;

              if (sourceTabId === targetTabId) return items;

              let draggedTab: WidgetV2 | undefined;
              let sourceColumnId: string | undefined;
              let targetColumnId: string | undefined;
              let targetIndex = 0;

              for (const app of items) {
                const sourceTab = app.tabs.find(t => t.id === sourceTabId);
                if (sourceTab) {
                  draggedTab = sourceTab;
                  sourceColumnId = app.id;
                }

                const targetIdx = app.tabs.findIndex(t => t.id === targetTabId);
                if (targetIdx !== -1) {
                  targetColumnId = app.id;
                  targetIndex = targetIdx;
                }
              }

              if (!draggedTab || !sourceColumnId || !targetColumnId) return items;

              // Ta sama column - arrayMove
              if (sourceColumnId === targetColumnId) {
                return items.map(app => {
                  if (app.id !== sourceColumnId) return app;
                  const sourceIndex = app.tabs.findIndex(t => t.id === sourceTabId);
                  return {
                    ...app,
                    tabs: arrayMove(app.tabs, sourceIndex, targetIndex)
                  };
                });
              }

              // Różne columns - przenieś tab
              return items.map(app => {
                if (app.id === sourceColumnId) {
                  return { ...app, tabs: app.tabs.filter(t => t.id !== sourceTabId) };
                }
                if (app.id === targetColumnId) {
                  const newTabs = [...app.tabs];
                  newTabs.splice(targetIndex, 0, draggedTab);
                  return { ...app, tabs: newTabs };
                }
                return app;
              });
            });
          }
        }
      }}
      onDragStart={() => {
        setIsDragging(true);
      }}
    >
      <Box
        ref={container}
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: '1fr',
            lg: `repeat(2, 1fr)`
          }
        }}
      >
        {marketTabApps.map((app, index) => (
          <Column
            key={app.id}
            app={app}
            container={container}
            currentDashboardName={dashboardName}
            currentTabName={currentTabName}
            id={app.id}
            index={index}
            items={app.tabs}
            onMoveAppToDraft={handleMoveAppToDraft}
            onMoveToDraft={handleMoveToDraft}
            onRemoveApp={removeId => {
              setMarketTabApps(items => items.filter(a => a.id !== removeId));
            }}
            onRemoveItem={removeTabId => {
              setMarketTabApps(items =>
                items
                  .map(a =>
                    a.id === app.id ? { ...a, tabs: a.tabs.filter(t => t.id !== removeTabId) } : a
                  )
                  .filter(a => a.tabs.length > 0)
              );
            }}
          />
        ))}
        {shouldShowBlankColumn && <BlankColumn />}
      </Box>
    </DragDropProvider>
  );
};

export default AppsOrder;
